// Auto-generated by scripts/split_calculators.py — keeps deterministic oilfield formulas in per-category modules.
import { round, num, positive, toRad } from "./_common";
// eslint-disable-next-line no-unused-vars
const _imports_ok = { round, num, positive, toRad };

// ===== Category: drilling =====
// --- Drilling ---
// =========================================================================
// DRILLING
// =========================================================================

export function hydrostaticPressure({ mw, tvd }) {
  const M = positive(num(mw, "Mud Weight"), "Mud Weight");
  const D = positive(num(tvd, "TVD"), "TVD");
  const p = 0.052 * M * D;
  return {
    result: round(p, 2),
    unit: "psi",
    formula: "P = 0.052 × MW × TVD",
    steps: [
      `MW = ${M} ppg, TVD = ${D} ft`,
      `P = 0.052 × ${M} × ${D}`,
      `P = ${round(p, 2)} psi`,
    ],
    inputs: { "Mud Weight (ppg)": M, "TVD (ft)": D },
  };
}

export function pressureGradient({ mw }) {
  const M = positive(num(mw, "Mud Weight"), "Mud Weight");
  const g = 0.052 * M;
  return {
    result: round(g, 4),
    unit: "psi/ft",
    formula: "Gradient = 0.052 × MW",
    steps: [`Gradient = 0.052 × ${M}`, `= ${round(g, 4)} psi/ft`],
    inputs: { "Mud Weight (ppg)": M },
  };
}

export function holeVolume({ diameter, length }) {
  const D = positive(num(diameter, "Hole Diameter"), "Hole Diameter");
  const L = positive(num(length, "Length"), "Length");
  const v = (D * D / 1029.4) * L;
  return {
    result: round(v, 3),
    unit: "bbl",
    formula: "V = (Dh² / 1029.4) × L",
    steps: [
      `Dh = ${D} in, L = ${L} ft`,
      `V = (${D}² / 1029.4) × ${L}`,
      `V = ${round(D * D / 1029.4, 5)} × ${L}`,
      `V = ${round(v, 3)} bbl`,
    ],
    inputs: { "Hole Diameter (in)": D, "Length (ft)": L },
  };
}

export function pipeCapacity({ id, length }) {
  const I = positive(num(id, "Pipe ID"), "Pipe ID");
  const L = positive(num(length, "Length"), "Length");
  const cap = (I * I) / 1029.4;
  const v = cap * L;
  return {
    result: round(v, 3),
    unit: "bbl",
    formula: "V = (ID² / 1029.4) × L  ;  Capacity = ID²/1029.4 bbl/ft",
    steps: [
      `ID = ${I} in, L = ${L} ft`,
      `Capacity = ${I}² / 1029.4 = ${round(cap, 5)} bbl/ft`,
      `V = ${round(cap, 5)} × ${L} = ${round(v, 3)} bbl`,
    ],
    inputs: { "Pipe ID (in)": I, "Length (ft)": L },
  };
}

export function annularCapacity({ dh, dp, length }) {
  const Dh = positive(num(dh, "Hole/Casing ID"), "Hole/Casing ID");
  const Dp = positive(num(dp, "Pipe OD"), "Pipe OD");
  if (Dp >= Dh) throw new Error("Pipe OD must be smaller than hole/casing ID");
  const L = positive(num(length, "Length"), "Length");
  const cap = (Dh * Dh - Dp * Dp) / 1029.4;
  const v = cap * L;
  return {
    result: round(v, 3),
    unit: "bbl",
    formula: "V = ((Dh² − Dp²) / 1029.4) × L",
    steps: [
      `Dh = ${Dh} in, Dp = ${Dp} in, L = ${L} ft`,
      `Capacity = (${Dh}² − ${Dp}²) / 1029.4 = ${round(cap, 5)} bbl/ft`,
      `V = ${round(cap, 5)} × ${L} = ${round(v, 3)} bbl`,
    ],
    inputs: { "Hole/Csg ID (in)": Dh, "Pipe OD (in)": Dp, "Length (ft)": L },
  };
}

export function pumpOutput({ pumpType, linerId, stroke, rodOd, efficiency }) {
  const t = (pumpType || "triplex").toLowerCase();
  const ID = positive(num(linerId, "Liner ID"), "Liner ID");
  const S = positive(num(stroke, "Stroke Length"), "Stroke Length");
  const eff = positive(num(efficiency || 1, "Efficiency"), "Efficiency");
  if (t === "duplex") {
    const R = positive(num(rodOd, "Rod OD"), "Rod OD");
    // Duplex: bbl/stk = 0.000162 × S × (2×ID² − R²)  (approx, single action both sides)
    const out = 0.000162 * S * (2 * ID * ID - R * R) * eff;
    return {
      result: round(out, 5),
      unit: "bbl/stk",
      formula: "Duplex: 0.000162 × Stroke × (2·ID² − Rod²) × eff",
      steps: [
        `ID=${ID}", Stroke=${S}", Rod=${R}", Eff=${eff}`,
        `= 0.000162 × ${S} × (2×${ID}² − ${R}²) × ${eff}`,
        `= ${round(out, 5)} bbl/stk`,
      ],
      inputs: { Type: "Duplex", "Liner ID (in)": ID, "Stroke (in)": S, "Rod OD (in)": R, Efficiency: eff },
    };
  }
  // Triplex: bbl/stk = 0.000243 × ID² × Stroke × eff
  const out = 0.000243 * ID * ID * S * eff;
  return {
    result: round(out, 5),
    unit: "bbl/stk",
    formula: "Triplex: 0.000243 × ID² × Stroke × eff",
    steps: [
      `ID=${ID}", Stroke=${S}", Eff=${eff}`,
      `= 0.000243 × ${ID}² × ${S} × ${eff}`,
      `= ${round(out, 5)} bbl/stk`,
    ],
    inputs: { Type: "Triplex", "Liner ID (in)": ID, "Stroke (in)": S, Efficiency: eff },
  };
}

export function strokesToBit({ pipeVolume, pumpBblStk }) {
  const V = positive(num(pipeVolume, "Pipe Internal Volume"), "Pipe Internal Volume");
  const Q = positive(num(pumpBblStk, "Pump Output"), "Pump Output");
  const s = V / Q;
  return {
    result: Math.round(s),
    unit: "strokes",
    formula: "Strokes to Bit = Pipe Volume / Pump Output (bbl/stk)",
    steps: [
      `V = ${V} bbl, Q = ${Q} bbl/stk`,
      `Strokes = ${V} / ${Q} = ${round(s, 1)}`,
      `≈ ${Math.round(s)} strokes`,
    ],
    inputs: { "Pipe Volume (bbl)": V, "Pump Output (bbl/stk)": Q },
  };
}

export function bottomsUpStrokes({ annularVolume, pumpBblStk }) {
  const V = positive(num(annularVolume, "Annular Volume"), "Annular Volume");
  const Q = positive(num(pumpBblStk, "Pump Output"), "Pump Output");
  const s = V / Q;
  return {
    result: Math.round(s),
    unit: "strokes",
    formula: "Bottoms-Up Strokes = Annular Volume / Pump Output",
    steps: [
      `Vann = ${V} bbl, Q = ${Q} bbl/stk`,
      `Strokes = ${V} / ${Q} = ${round(s, 1)}`,
      `≈ ${Math.round(s)} strokes`,
    ],
    inputs: { "Annular Volume (bbl)": V, "Pump Output (bbl/stk)": Q },
  };
}

export function circulationTime({ totalVolume, flowRate }) {
  const V = positive(num(totalVolume, "Total Volume"), "Total Volume");
  const Q = positive(num(flowRate, "Flow Rate"), "Flow Rate");
  const t = V / Q;
  return {
    result: round(t, 2),
    unit: "min",
    formula: "Time = Total Volume / Flow Rate",
    steps: [
      `V = ${V} bbl, Q = ${Q} bbl/min`,
      `t = ${V} / ${Q} = ${round(t, 2)} min`,
    ],
    inputs: { "Total Volume (bbl)": V, "Flow Rate (bbl/min)": Q },
  };
}

// --- v1.4 — Drilling additions ---
// ----- Drilling additions -----

export function drillStringVolume({ id, length }) {
  // Same math as pipeCapacity, framed for drill string column volume
  const I = positive(num(id, "Drill String ID"), "Drill String ID");
  const L = positive(num(length, "Length"), "Length");
  const v = (I * I / 1029.4) * L;
  return {
    result: round(v, 3),
    unit: "bbl",
    formula: "V = ID² × L / 1029.4",
    steps: [
      `ID = ${I} in, L = ${L} ft`,
      `V = (${I}² × ${L}) / 1029.4 = ${round(v, 3)} bbl`,
    ],
    inputs: { "Drill String ID (in)": I, "Length (ft)": L },
  };
}

export function rateOfPenetration({ feetDrilled, hours }) {
  const F = positive(num(feetDrilled, "Feet Drilled"), "Feet Drilled");
  const H = positive(num(hours, "Hours"), "Hours");
  const rop = F / H;
  return {
    result: round(rop, 2),
    unit: "ft/hr",
    formula: "ROP = Feet Drilled / Time",
    steps: [`ROP = ${F} / ${H} = ${round(rop, 2)} ft/hr`],
    inputs: { "Feet Drilled": F, "Time (hr)": H },
  };
}

export function lagTime({ annularVolume, flowRate }) {
  const V = positive(num(annularVolume, "Annular Volume"), "Annular Volume");
  const Q = positive(num(flowRate, "Flow Rate"), "Flow Rate");
  const t = V / Q;
  return {
    result: round(t, 2),
    unit: "min",
    formula: "Lag Time = Annular Volume bit→surface / Flow Rate",
    steps: [
      `V_ann = ${V} bbl, Q = ${Q} bbl/min`,
      `Lag = ${V} / ${Q} = ${round(t, 2)} min  ( ≈ ${round(t / 60, 2)} hr )`,
    ],
    inputs: { "Annular Volume (bbl)": V, "Flow Rate (bbl/min)": Q },
    extras: { "Lag (hr)": round(t / 60, 2) },
  };
}

// (comingSoonFn moved to _common.js — see top-level import)

// =========================================================================
// STAGE 1 — GEOMETRY, VOLUMES & CAPACITIES (drilling expansion v1.8)
// =========================================================================

const CAP = (ID, L) => (ID * ID * L) / 1029.4;          // capacity bbl
const DISP = (OD, ID, L) => ((OD * OD - ID * ID) * L) / 1029.4; // steel displacement bbl

export function drillCollarCapacity({ id, length }) {
  const I = positive(num(id, "DC ID"), "DC ID");
  const L = positive(num(length, "Length"), "Length");
  const v = CAP(I, L);
  return {
    result: round(v, 3), unit: "bbl",
    formula: "V_DC_int = ID² × L / 1029.4",
    steps: [`(${I}² × ${L}) / 1029.4 = ${round(v, 3)} bbl`],
    inputs: { "DC ID (in)": I, "Length (ft)": L },
  };
}

export function drillCollarDisplacement({ od, id, length }) {
  const O = positive(num(od, "DC OD"), "DC OD");
  const I = num(id, "DC ID"); if (I < 0 || I >= O) throw new Error("DC ID must be 0 ≤ ID < OD");
  const L = positive(num(length, "Length"), "Length");
  const v = DISP(O, I, L);
  return {
    result: round(v, 3), unit: "bbl",
    formula: "Disp = (OD² − ID²) × L / 1029.4",
    steps: [`(${O}² − ${I}²) × ${L} / 1029.4 = ${round(v, 3)} bbl`],
    inputs: { "OD (in)": O, "ID (in)": I, "Length (ft)": L },
  };
}

export const hwdpCapacity = drillCollarCapacity;
export const hwdpDisplacement = drillCollarDisplacement;
export const drillPipeCapacity = drillCollarCapacity;
export const drillPipeDisplacement = drillCollarDisplacement;
export const casingToDpAnnular = ({ dh, dp, length }) => annularCapacity({ dh, dp, length });
export const openHoleToDcAnnular = ({ dh, dp, length }) => annularCapacity({ dh, dp, length });
export const openHoleToHwdpAnnular = ({ dh, dp, length }) => annularCapacity({ dh, dp, length });

// Multi-section helpers: parse CSV "label,ID,length" or "label,OD,ID,length" lines.
function parseRows(csv, expectedCols, label) {
  if (!csv || typeof csv !== "string") throw new Error(`Provide CSV: ${label}`);
  const rows = csv.split(/\r?\n/).map((l) => l.trim()).filter((l) => l && !l.startsWith("#"));
  if (rows.length === 0) throw new Error(`At least one row required: ${label}`);
  return rows.map((r, i) => {
    const parts = r.split(/[,\s]+/);
    if (parts.length < expectedCols) throw new Error(`Row ${i + 1}: need ${expectedCols} fields`);
    return parts;
  });
}

export function multiSectionDrillStringCapacity({ sectionsCsv }) {
  const rows = parseRows(sectionsCsv, 3, "Drill string sections (label,ID,length per line)");
  let total = 0; const lines = [];
  for (const r of rows) {
    const lbl = r[0]; const ID = Number(r[1]); const L = Number(r[2]);
    if (!isFinite(ID) || !isFinite(L) || ID <= 0 || L <= 0) throw new Error(`Bad row: ${r.join(",")}`);
    const v = CAP(ID, L); total += v;
    lines.push(`${lbl}: ID=${ID}", L=${L}ft → ${round(v, 3)} bbl`);
  }
  return {
    result: round(total, 3), unit: "bbl (total internal)",
    formula: "Σ (ID² × L / 1029.4)  per section",
    steps: [...lines, `Σ = ${round(total, 3)} bbl`],
    inputs: { "Sections": rows.length },
    extras: { "Total Sections": rows.length },
  };
}

export function totalDrillStringVolume({ dpVolume, hwdpVolume, dcVolume }) {
  const A = positive(num(dpVolume, "DP Volume"), "DP Volume");
  const B = num(hwdpVolume ?? 0, "HWDP Volume"); if (B < 0) throw new Error("HWDP ≥ 0");
  const C = num(dcVolume ?? 0, "DC Volume"); if (C < 0) throw new Error("DC ≥ 0");
  const T = A + B + C;
  return {
    result: round(T, 3), unit: "bbl",
    formula: "V_string = V_DP + V_HWDP + V_DC",
    steps: [`${A} + ${B} + ${C} = ${round(T, 3)} bbl`],
    inputs: { "DP (bbl)": A, "HWDP (bbl)": B, "DC (bbl)": C },
  };
}

export function totalDrillStringDisplacement({ dpDisp, hwdpDisp, dcDisp }) {
  const A = positive(num(dpDisp, "DP Disp"), "DP Disp");
  const B = num(hwdpDisp ?? 0, "HWDP Disp"); if (B < 0) throw new Error("HWDP ≥ 0");
  const C = num(dcDisp ?? 0, "DC Disp"); if (C < 0) throw new Error("DC ≥ 0");
  const T = A + B + C;
  return {
    result: round(T, 3), unit: "bbl (steel displacement)",
    formula: "Disp_string = Disp_DP + Disp_HWDP + Disp_DC",
    steps: [`${A} + ${B} + ${C} = ${round(T, 3)} bbl`],
    inputs: { "DP (bbl)": A, "HWDP (bbl)": B, "DC (bbl)": C },
  };
}

export function totalActiveCirculatingVolume({ drillStringVol, annularVol, surfaceVol }) {
  const A = positive(num(drillStringVol, "Drill String Vol"), "Drill String Vol");
  const B = positive(num(annularVol, "Annular Vol"), "Annular Vol");
  const S = num(surfaceVol ?? 0, "Surface Vol"); if (S < 0) throw new Error("Surface ≥ 0");
  const T = A + B + S;
  return {
    result: round(T, 2), unit: "bbl",
    formula: "V_active = V_string + V_annular + V_surface",
    steps: [`${A} + ${B} + ${S} = ${round(T, 2)} bbl`],
    inputs: { "String (bbl)": A, "Annular (bbl)": B, "Surface (bbl)": S },
  };
}

// =========================================================================
// STAGE 2 — Mechanical Specific Energy (Teale) & Bingham pipe pressure drop
// Both are deterministic estimates. MSE is a drilling-performance indicator;
// the Bingham pipe drop is a simplified laminar-regime field estimate only.
// =========================================================================

// Teale's Mechanical Specific Energy (MSE) — drilling performance indicator.
// MSE (psi) = WOB / Ab + (120·π·N·T) / (Ab·ROP)
//   WOB  : weight on bit (lbf)
//   Ab   : bit cross-sectional area (in²) = π/4 · D²
//   N    : RPM (rev/min)
//   T    : surface torque (ft-lbf)
//   ROP  : rate of penetration (ft/hr)
// Result is in psi. The numerical constant 120·π already carries the rev→2π and
// hr→min unit conversions so that ft-lbf · rev/min ÷ (in² · ft/hr) reduces to psi.
export function mse({ wob, bitDiameter, rpm, torque, rop }) {
  const W = positive(num(wob, "WOB"), "WOB");
  const D = positive(num(bitDiameter, "Bit Diameter"), "Bit Diameter");
  const N = positive(num(rpm, "RPM"), "RPM");
  const T = positive(num(torque, "Torque"), "Torque");
  const R = positive(num(rop, "ROP"), "ROP");
  const Ab = (Math.PI / 4) * D * D;            // in²
  const term1 = W / Ab;                         // psi
  const term2 = (120 * Math.PI * N * T) / (Ab * R); // psi
  const m = term1 + term2;
  return {
    result: round(m, 0),
    unit: "psi",
    formula: "MSE = WOB/Ab + (120·π·N·T)/(Ab·ROP)   ;  Ab = π/4 · D²",
    steps: [
      `Ab = π/4 × ${D}² = ${round(Ab, 3)} in²`,
      `Axial term = ${W} / ${round(Ab, 3)} = ${round(term1, 1)} psi`,
      `Rotary term = (120·π × ${N} × ${T}) / (${round(Ab, 3)} × ${R}) = ${round(term2, 1)} psi`,
      `MSE = ${round(term1, 1)} + ${round(term2, 1)} = ${round(m, 0)} psi`,
    ],
    inputs: { "WOB (lbf)": W, "Bit Ø (in)": D, "RPM": N, "Torque (ft-lbf)": T, "ROP (ft/hr)": R },
    extras: {
      "Bit Area Ab (in²)": round(Ab, 3),
      "Axial term (psi)": round(term1, 0),
      "Rotary term (psi)": round(term2, 0),
      "MSE (ksi)": round(m / 1000, 2),
      "Status": "Advanced / Requires Interpretation",
      "Note": "MSE is a performance indicator only. Interpretation depends on bit type, formation, drilling mode, measurement quality, vibration, hydraulics, ROP, WOB, torque, and operating context. Trends are usually more useful than single values.",
    },
    worksheetWarning: true,
  };
}

// Drill Pipe Pressure Drop — Simplified Bingham-plastic LAMINAR estimate inside pipe.
// V  (ft/min)   = 24.51 · Q / d²        (d = pipe ID in., Q = gpm)
// ΔP (psi)      = (PV · V · L) / (1500 · d²) + (YP · L) / (225 · d)
//   PV  : plastic viscosity (cP)
//   YP  : yield point (lb/100 ft²)
//   L   : pipe length (ft)
//   d   : pipe ID (in)
//   MW  : mud weight (ppg) — used only to estimate the Bingham Reynolds number for regime guidance.
// Regime check (Bingham apparent viscosity):
//   μa = PV + 6.65 · YP · d / V    (cP)
//   Re = 928 · MW · V · d / μa
// Re < ~2,100 → laminar (formula valid); Re ≥ 2,100 → turbulent (estimate is not valid, requires hydraulics software).
export function drillPipePressureDrop({ mw, pv, yp, flowRate, pipeId, pipeLength }) {
  const M = positive(num(mw, "Mud Weight"), "Mud Weight");
  const PV = positive(num(pv, "Plastic Viscosity"), "Plastic Viscosity");
  const YP = num(yp, "Yield Point"); if (YP < 0) throw new Error("Yield Point must be ≥ 0");
  const Q = positive(num(flowRate, "Flow Rate"), "Flow Rate");
  const d = positive(num(pipeId, "Pipe ID"), "Pipe ID");
  const L = positive(num(pipeLength, "Pipe Length"), "Pipe Length");
  const V = (24.51 * Q) / (d * d);             // ft/min
  const dpVisc = (PV * V * L) / (1500 * d * d); // psi
  const dpYp   = (YP * L) / (225 * d);          // psi
  const dp = dpVisc + dpYp;
  // Bingham apparent viscosity at the wall + Reynolds (guide only)
  const muA = PV + (6.65 * YP * d) / Math.max(V, 1e-6); // cP
  const Re = (928 * M * V * d) / Math.max(muA, 1e-6);
  const regime = Re < 2100 ? "Laminar (formula valid)" : Re < 4000 ? "Transitional (estimate uncertain)" : "Turbulent (Bingham laminar formula NOT valid)";
  return {
    result: round(dp, 1),
    unit: "psi",
    formula: "ΔP_pipe ≈ (PV·V·L)/(1500·d²) + (YP·L)/(225·d)   ;  V = 24.51·Q/d²",
    steps: [
      `V = 24.51 × ${Q} / ${d}² = ${round(V, 1)} ft/min`,
      `Viscous term = (${PV} × ${round(V, 1)} × ${L}) / (1500 × ${d}²) = ${round(dpVisc, 1)} psi`,
      `Yield-stress term = (${YP} × ${L}) / (225 × ${d}) = ${round(dpYp, 1)} psi`,
      `ΔP ≈ ${round(dpVisc, 1)} + ${round(dpYp, 1)} = ${round(dp, 1)} psi`,
      `Regime check: μa = ${round(muA, 2)} cP, Re ≈ ${round(Re, 0)} → ${regime}`,
    ],
    inputs: { "MW (ppg)": M, "PV (cP)": PV, "YP (lb/100ft²)": YP, "Q (gpm)": Q, "ID (in)": d, "L (ft)": L },
    extras: {
      "Velocity V (ft/min)": round(V, 1),
      "Apparent μa (cP)": round(muA, 2),
      "Reynolds Re": round(Re, 0),
      "Flow regime": regime,
    },
    worksheetWarning: true,
  };
}


// =========================================================================
// STAGE 3 — Per-foot capacities & stroke/volume helpers (deterministic).
// =========================================================================

// Hole Capacity per foot:  C_hole (bbl/ft) = Dh² / 1029.4
export function holeCapacityPerFoot({ diameter }) {
  const D = positive(num(diameter, "Hole Diameter"), "Hole Diameter");
  const c = (D * D) / 1029.4;
  return {
    result: round(c, 5),
    unit: "bbl/ft",
    formula: "C_hole = Dh² / 1029.4",
    steps: [
      `Dh = ${D} in`,
      `C_hole = ${D}² / 1029.4`,
      `C_hole = ${round(c, 5)} bbl/ft`,
    ],
    inputs: { "Hole Diameter (in)": D },
  };
}

// Pipe Capacity per foot:  C_pipe (bbl/ft) = ID² / 1029.4
export function pipeCapacityPerFoot({ id }) {
  const I = positive(num(id, "Pipe ID"), "Pipe ID");
  const c = (I * I) / 1029.4;
  return {
    result: round(c, 5),
    unit: "bbl/ft",
    formula: "C_pipe = ID² / 1029.4",
    steps: [
      `ID = ${I} in`,
      `C_pipe = ${I}² / 1029.4`,
      `C_pipe = ${round(c, 5)} bbl/ft`,
    ],
    inputs: { "Pipe ID (in)": I },
  };
}

// Annular Capacity per foot:  C_ann (bbl/ft) = (Dh² − Dp²) / 1029.4
export function annularCapacityPerFoot({ dh, dp }) {
  const Dh = positive(num(dh, "Hole Diameter"), "Hole Diameter");
  const Dp = positive(num(dp, "Pipe OD"), "Pipe OD");
  if (Dp >= Dh) throw new Error("Pipe OD must be smaller than hole diameter");
  const c = (Dh * Dh - Dp * Dp) / 1029.4;
  return {
    result: round(c, 5),
    unit: "bbl/ft",
    formula: "C_ann = (Dh² − Dp²) / 1029.4",
    steps: [
      `Dh = ${Dh} in,  Dp = ${Dp} in`,
      `C_ann = (${Dh}² − ${Dp}²) / 1029.4`,
      `C_ann = ${round(c, 5)} bbl/ft`,
    ],
    inputs: { "Hole Diameter (in)": Dh, "Pipe OD (in)": Dp },
  };
}

// Strokes Required for Selected Volume:  S = V / Q   (Q in bbl/stroke)
export function strokesForVolume({ volume, pumpBblStk }) {
  const V = positive(num(volume, "Volume"), "Volume");
  const Q = positive(num(pumpBblStk, "Pump Output"), "Pump Output");
  const s = V / Q;
  return {
    result: Math.round(s),
    unit: "strokes",
    formula: "Strokes = Volume / Pump Output (bbl/stk)",
    steps: [
      `V = ${V} bbl,  Q = ${Q} bbl/stk`,
      `Strokes = ${V} / ${Q} = ${round(s, 2)}`,
      `≈ ${Math.round(s)} strokes`,
    ],
    inputs: { "Volume (bbl)": V, "Pump Output (bbl/stk)": Q },
    extras: { "Strokes (exact)": round(s, 2) },
  };
}

// Volume Pumped from Strokes:  V (bbl) = Strokes × Q
export function volumePumpedFromStrokes({ strokes, pumpBblStk }) {
  const S = positive(num(strokes, "Strokes"), "Strokes");
  const Q = positive(num(pumpBblStk, "Pump Output"), "Pump Output");
  const V = S * Q;
  return {
    result: round(V, 3),
    unit: "bbl",
    formula: "Volume Pumped = Strokes × Pump Output (bbl/stk)",
    steps: [
      `S = ${S} strokes,  Q = ${Q} bbl/stk`,
      `V = ${S} × ${Q} = ${round(V, 3)} bbl`,
    ],
    inputs: { "Strokes": S, "Pump Output (bbl/stk)": Q },
  };
}


// =========================================================================
// STAGE 4 — Pump rate, circulation times, and total circulation strokes.
// =========================================================================

// Pump Rate from SPM:  Q (bbl/min) = Pump Output (bbl/stk) × SPM
export function pumpRateFromSpm({ pumpBblStk, spm }) {
  const Q = positive(num(pumpBblStk, "Pump Output"), "Pump Output");
  const N = positive(num(spm, "Strokes Per Minute"), "Strokes Per Minute");
  const r = Q * N;
  return {
    result: round(r, 3),
    unit: "bbl/min",
    formula: "Pump Rate = Pump Output (bbl/stk) × SPM",
    steps: [
      `Q = ${Q} bbl/stk,  SPM = ${N}`,
      `Pump Rate = ${Q} × ${N} = ${round(r, 3)} bbl/min`,
    ],
    inputs: { "Pump Output (bbl/stk)": Q, "SPM": N },
    extras: { "Pump Rate (gpm)": round(r * 42, 2) },
  };
}

// Time to Pump Selected Volume:  t (min) = V / Q
export function timeToPumpVolume({ volume, pumpRate }) {
  const V = positive(num(volume, "Volume"), "Volume");
  const Q = positive(num(pumpRate, "Pump Rate"), "Pump Rate");
  const tMin = V / Q;
  const tHr = tMin / 60;
  return {
    result: round(tMin, 2),
    unit: "min",
    formula: "Time = Volume / Pump Rate",
    steps: [
      `V = ${V} bbl,  Q = ${Q} bbl/min`,
      `Time = ${V} / ${Q} = ${round(tMin, 2)} min`,
      `Time = ${round(tHr, 2)} hr`,
    ],
    inputs: { "Volume (bbl)": V, "Pump Rate (bbl/min)": Q },
    extras: { "Time (hr)": round(tHr, 2) },
  };
}

// Full Circulation Time:  t (min) = V_total / Q
export function fullCirculationTime({ totalVolume, pumpRate }) {
  const V = positive(num(totalVolume, "Total Circulating Volume"), "Total Circulating Volume");
  const Q = positive(num(pumpRate, "Pump Rate"), "Pump Rate");
  const tMin = V / Q;
  const tHr = tMin / 60;
  return {
    result: round(tMin, 2),
    unit: "min",
    formula: "Full Circulation Time = Total Circulating Volume / Pump Rate",
    steps: [
      `V_total = ${V} bbl,  Q = ${Q} bbl/min`,
      `t = ${V} / ${Q} = ${round(tMin, 2)} min`,
      `t = ${round(tHr, 2)} hr`,
    ],
    inputs: { "Total Volume (bbl)": V, "Pump Rate (bbl/min)": Q },
    extras: { "Time (hr)": round(tHr, 2) },
  };
}

// Bottoms-Up Time:  t (min) = V_annular / Q
export function bottomsUpTime({ annularVolume, pumpRate }) {
  const V = positive(num(annularVolume, "Annular Volume"), "Annular Volume");
  const Q = positive(num(pumpRate, "Pump Rate"), "Pump Rate");
  const tMin = V / Q;
  const tHr = tMin / 60;
  return {
    result: round(tMin, 2),
    unit: "min",
    formula: "Bottoms-Up Time = Annular Volume / Pump Rate",
    steps: [
      `V_ann = ${V} bbl,  Q = ${Q} bbl/min`,
      `t = ${V} / ${Q} = ${round(tMin, 2)} min`,
      `t = ${round(tHr, 2)} hr`,
    ],
    inputs: { "Annular Volume (bbl)": V, "Pump Rate (bbl/min)": Q },
    extras: { "Time (hr)": round(tHr, 2) },
  };
}

// Total Circulation Strokes = Drill String Strokes + Annular Strokes
export function totalCirculationStrokes({ drillStringStrokes, annularStrokes }) {
  const D = positive(num(drillStringStrokes, "Drill String Strokes"), "Drill String Strokes");
  const A = positive(num(annularStrokes, "Annular Strokes"), "Annular Strokes");
  const T = D + A;
  return {
    result: Math.round(T),
    unit: "strokes",
    formula: "Total Circulation Strokes = Drill String Strokes + Annular Strokes",
    steps: [
      `Drill String Strokes = ${D}`,
      `Annular Strokes = ${A}`,
      `Total = ${D} + ${A} = ${Math.round(T)} strokes`,
    ],
    inputs: { "Drill String Strokes": D, "Annular Strokes": A },
  };
}


// =========================================================================
// STAGE 5 — Mud weight / pressure / TVD interconversions (0.052 family).
// =========================================================================

// Mud Weight from Pressure and TVD:  MW (ppg) = P / (0.052 × TVD)
export function mudWeightFromPressure({ pressure, tvd }) {
  const P = positive(num(pressure, "Pressure"), "Pressure");
  const D = positive(num(tvd, "TVD"), "TVD");
  const mw = P / (0.052 * D);
  return {
    result: round(mw, 2),
    unit: "ppg",
    formula: "MW = P / (0.052 × TVD)",
    steps: [
      `P = ${P} psi,  TVD = ${D} ft`,
      `MW = ${P} / (0.052 × ${D})`,
      `MW = ${round(mw, 2)} ppg`,
    ],
    inputs: { "Pressure (psi)": P, "TVD (ft)": D },
  };
}

// TVD from Pressure and Mud Weight:  TVD (ft) = P / (0.052 × MW)
// Mud Column Height Required for Pressure:  h_mud (ft) = P / (0.052 × MW)
// Note: this is NOT the actual well TVD — it is the vertical mud-column height
// needed to generate the chosen hydrostatic pressure at the chosen mud weight.
export function tvdFromPressure({ pressure, mw }) {
  const P = positive(num(pressure, "Pressure"), "Pressure");
  const M = positive(num(mw, "Mud Weight"), "Mud Weight");
  const D = P / (0.052 * M);
  return {
    result: round(D, 2),
    unit: "ft",
    formula: "Mud Column Height = P / (0.052 × MW)",
    steps: [
      `P = ${P} psi,  MW = ${M} ppg`,
      `Mud Column Height = ${P} / (0.052 × ${M})`,
      `Mud Column Height = ${round(D, 2)} ft`,
    ],
    inputs: { "Pressure (psi)": P, "Mud Weight (ppg)": M },
    extras: {
      "Note": "This is the mud-column height required to produce the selected pressure at the selected mud weight. It is not the actual well TVD.",
    },
  };
}

// Equivalent Mud Weight:  EMW (ppg) = P / (0.052 × TVD)
// Same form as MW-from-pressure but framed as "equivalent" for pressure conditions
// at depth (e.g. ECD, fracture pressure expressed as a mud weight).
export function equivalentMudWeight({ pressure, tvd }) {
  const P = positive(num(pressure, "Pressure"), "Pressure");
  const D = positive(num(tvd, "TVD"), "TVD");
  const emw = P / (0.052 * D);
  return {
    result: round(emw, 2),
    unit: "ppg",
    formula: "EMW = P / (0.052 × TVD)",
    steps: [
      `P = ${P} psi,  TVD = ${D} ft`,
      `EMW = ${P} / (0.052 × ${D})`,
      `EMW = ${round(emw, 2)} ppg`,
    ],
    inputs: { "Pressure (psi)": P, "TVD (ft)": D },
  };
}

// Surface Pressure Equivalent to Mud Weight Increase:  ΔP = 0.052 × ΔMW × TVD
export function pressureFromMudWeightIncrease({ mwIncrease, tvd }) {
  const dMW = positive(num(mwIncrease, "Mud Weight Increase"), "Mud Weight Increase");
  const D   = positive(num(tvd, "TVD"), "TVD");
  const dP = 0.052 * dMW * D;
  return {
    result: round(dP, 2),
    unit: "psi",
    formula: "ΔP = 0.052 × ΔMW × TVD",
    steps: [
      `ΔMW = ${dMW} ppg,  TVD = ${D} ft`,
      `ΔP = 0.052 × ${dMW} × ${D}`,
      `ΔP = ${round(dP, 2)} psi`,
    ],
    inputs: { "ΔMW (ppg)": dMW, "TVD (ft)": D },
  };
}

// Mud Weight Increase Required for Pressure:  ΔMW = ΔP / (0.052 × TVD)
export function mudWeightIncreaseForPressure({ pressureIncrease, tvd }) {
  const dP = positive(num(pressureIncrease, "Pressure Increase"), "Pressure Increase");
  const D  = positive(num(tvd, "TVD"), "TVD");
  const dMW = dP / (0.052 * D);
  return {
    result: round(dMW, 2),
    unit: "ppg",
    formula: "ΔMW = ΔP / (0.052 × TVD)",
    steps: [
      `ΔP = ${dP} psi,  TVD = ${D} ft`,
      `ΔMW = ${dP} / (0.052 × ${D})`,
      `ΔMW = ${round(dMW, 2)} ppg`,
    ],
    inputs: { "ΔP (psi)": dP, "TVD (ft)": D },
  };
}

// =========================================================================
// STAGE 6 — Drilling time, depth, efficiency, and connection helpers.
// =========================================================================

// Time to Drill Interval:  t (hr) = Interval (ft) / ROP (ft/hr)
export function timeToDrillInterval({ interval, rop }) {
  const L = positive(num(interval, "Interval"), "Interval");
  const R = positive(num(rop, "ROP"), "ROP");
  const tHr = L / R;
  const tDay = tHr / 24;
  return {
    result: round(tHr, 2),
    unit: "hr",
    formula: "Time = Interval / ROP",
    steps: [
      `Interval = ${L} ft,  ROP = ${R} ft/hr`,
      `Time = ${L} / ${R} = ${round(tHr, 2)} hr`,
      `Time = ${round(tDay, 2)} days`,
    ],
    inputs: { "Interval (ft)": L, "ROP (ft/hr)": R },
    extras: { "Time (days)": round(tDay, 2) },
  };
}

// Depth Drilled from ROP and Time:  D (ft) = ROP × t
export function depthDrilledFromRop({ rop, time }) {
  const R = positive(num(rop, "ROP"), "ROP");
  const T = positive(num(time, "Time"), "Time");
  const D = R * T;
  return {
    result: round(D, 2),
    unit: "ft",
    formula: "Depth Drilled = ROP × Time",
    steps: [
      `ROP = ${R} ft/hr,  Time = ${T} hr`,
      `Depth = ${R} × ${T} = ${round(D, 2)} ft`,
    ],
    inputs: { "ROP (ft/hr)": R, "Time (hr)": T },
  };
}

// Connection Time Total:  total_min = N × avg_min ;  total_hr = total_min / 60
export function connectionTimeTotal({ connections, avgConnectionMin }) {
  const N = positive(num(connections, "Number of Connections"), "Number of Connections");
  const A = positive(num(avgConnectionMin, "Average Connection Time"), "Average Connection Time");
  const totMin = N * A;
  const totHr = totMin / 60;
  return {
    result: round(totHr, 2),
    unit: "hr",
    formula: "Total Connection Time = N × Avg Connection Time / 60",
    steps: [
      `N = ${N} connections,  Avg = ${A} min/connection`,
      `Total = ${N} × ${A} = ${round(totMin, 2)} min`,
      `Total = ${round(totHr, 2)} hr`,
    ],
    inputs: { "Connections": N, "Avg (min)": A },
    extras: { "Total (min)": round(totMin, 2) },
  };
}

// Drilling Efficiency Percentage:  η = Net / Total × 100
export function drillingEfficiency({ netDrillingHours, totalHours }) {
  const Net = positive(num(netDrillingHours, "Net Drilling Time"), "Net Drilling Time");
  const Tot = positive(num(totalHours, "Total Time"), "Total Time");
  if (Net > Tot) throw new Error("Net Drilling Time cannot exceed Total Time");
  const eff = (Net / Tot) * 100;
  return {
    result: round(eff, 2),
    unit: "%",
    formula: "Drilling Efficiency = (Net Drilling Time / Total Time) × 100",
    steps: [
      `Net = ${Net} hr,  Total = ${Tot} hr`,
      `η = (${Net} / ${Tot}) × 100`,
      `η = ${round(eff, 2)} %`,
    ],
    inputs: { "Net (hr)": Net, "Total (hr)": Tot },
  };
}

// Feet Per Connection:  ft/conn = Total Feet / N
export function feetPerConnection({ totalFeet, connections }) {
  const F = positive(num(totalFeet, "Total Feet Drilled"), "Total Feet Drilled");
  const N = positive(num(connections, "Number of Connections"), "Number of Connections");
  const fpc = F / N;
  return {
    result: round(fpc, 2),
    unit: "ft/connection",
    formula: "Feet Per Connection = Total Feet Drilled / Number of Connections",
    steps: [
      `Total = ${F} ft,  N = ${N} connections`,
      `ft/conn = ${F} / ${N} = ${round(fpc, 2)} ft/connection`,
    ],
    inputs: { "Total Feet (ft)": F, "Connections": N },
  };
}


// =========================================================================
// STAGE 9 — Multi-Section Annular Volume & Multi-Section Drill String
//           Internal Volume (composable, multi-row inputs).
// =========================================================================

// Parse a free-form CSV-style multi-row table.
//   Lines beginning with "#" or empty lines are ignored.
//   Each row tokens are comma-separated and trimmed.
function _parseTable(raw) {
  const rows = [];
  const text = String(raw || "").trim();
  if (!text) throw new Error("At least one section row is required");
  text.split(/\r?\n/).forEach((line, i) => {
    const t = line.trim();
    if (!t || t.startsWith("#")) return;
    const tokens = t.split(",").map((x) => x.trim()).filter((x) => x.length > 0);
    if (tokens.length === 0) return;
    rows.push({ lineNo: i + 1, tokens });
  });
  if (rows.length === 0) throw new Error("At least one section row is required");
  return rows;
}

// Try to parse a token as a finite positive number.
function _maybeNum(tok) {
  if (tok === undefined || tok === null) return null;
  const n = parseFloat(tok);
  return Number.isFinite(n) ? n : null;
}

// Multi-Section Annular Volume.
// Each row may be one of:
//   1) "Name, BoundaryType, OuterID, TubularType, TubularOD, Length"   (6 tokens)
//   2) "OuterID, TubularOD, Length"                                    (3 tokens)
//   3) "OuterID, TubularOD, Length, Name"                              (4 tokens, name last)
// BoundaryType: "casing" | "openhole" | "open_hole" | "open-hole" | "casing_id"
// TubularType:  free-text label (drill_pipe / HWDP / drill_collar / tubing / casing / other)
// Per-section formula:  V_i (bbl) = (OuterID² − TubularOD²) × L / 1029.4
export function multiSectionAnnularVolume({ sections }) {
  const rows = _parseTable(sections);
  const stepLines = [];
  const perSection = {};
  let totalVol = 0;
  let totalLen = 0;
  const unrealistic = [];
  rows.forEach((r, idx) => {
    const t = r.tokens;
    let name = `Section ${idx + 1}`;
    let outerType = "—";
    let outerId, tubularType = "—", tubularOd, length;
    if (t.length >= 6 && _maybeNum(t[2]) !== null && _maybeNum(t[4]) !== null && _maybeNum(t[5]) !== null) {
      name = t[0] || name;
      outerType = (t[1] || "—").toLowerCase();
      outerId = _maybeNum(t[2]);
      tubularType = t[3] || "—";
      tubularOd = _maybeNum(t[4]);
      length = _maybeNum(t[5]);
    } else if (t.length === 3 || (t.length === 4 && _maybeNum(t[3]) === null)) {
      outerId = _maybeNum(t[0]);
      tubularOd = _maybeNum(t[1]);
      length = _maybeNum(t[2]);
      if (t[3]) name = t[3];
    } else {
      throw new Error(`Row ${idx + 1}: unable to parse — expected "name,boundaryType,outerID,tubularType,tubularOD,length" or "outerID,tubularOD,length".`);
    }
    if (outerId === null || tubularOd === null || length === null) {
      throw new Error(`Row ${idx + 1}: outer ID, tubular OD, and length must be numeric.`);
    }
    if (outerId <= 0) throw new Error(`Row ${idx + 1}: Outer ID must be > 0`);
    if (tubularOd <= 0) throw new Error(`Row ${idx + 1}: Tubular OD must be > 0`);
    if (length <= 0) throw new Error(`Row ${idx + 1}: Length must be > 0`);
    if (tubularOd >= outerId) {
      throw new Error(`Row ${idx + 1} (${name}): Tubular OD (${tubularOd}) must be less than Outer ID (${outerId}).`);
    }
    if (outerId > 36) unrealistic.push(`Row ${idx + 1}: Outer ID ${outerId} in is unusually large — please verify.`);
    if (length > 30000) unrealistic.push(`Row ${idx + 1}: Length ${length} ft is unusually long — please verify.`);
    const v = ((outerId * outerId) - (tubularOd * tubularOd)) * length / 1029.4;
    totalVol += v;
    totalLen += length;
    perSection[`${name} (bbl)`] = round(v, 3);
    stepLines.push(
      `Row ${idx + 1} · ${name} [${outerType} ${outerId} in / ${tubularType} ${tubularOd} in × ${length} ft] → (${outerId}² − ${tubularOd}²) × ${length} / 1029.4 = ${round(v, 3)} bbl`,
    );
  });
  const avgCap = totalLen > 0 ? totalVol / totalLen : 0;
  const extras = {
    "Sections counted": rows.length,
    "Total length (ft)": round(totalLen, 1),
    "Total annular volume (bbl)": round(totalVol, 3),
    "Avg annular capacity (bbl/ft)": round(avgCap, 5),
    ...perSection,
  };
  if (unrealistic.length) extras["Validation warnings"] = unrealistic.join("  |  ");
  return {
    result: round(totalVol, 3),
    unit: "bbl",
    formula: "Σ  V_i = (Outer ID² − Tubular OD²) × L_i / 1029.4   (one row per section)",
    steps: [
      "Per-section parse and volume:",
      ...stepLines,
      `Total annular volume = Σ V_i = ${round(totalVol, 3)} bbl over ${round(totalLen, 1)} ft`,
      `Average annular capacity = ${round(totalVol, 3)} / ${round(totalLen, 1)} = ${round(avgCap, 5)} bbl/ft`,
    ],
    inputs: { "Rows entered": rows.length },
    extras,
    worksheetWarning: true,
  };
}

// Multi-Section Drill String Internal Volume.
// Each row may be one of:
//   1) "ComponentType, ID, Length"  (3 tokens)
//   2) "ID, Length"                 (2 tokens)
// ComponentType: free-text label (Drill Pipe / HWDP / Drill Collar / BHA / Other)
// Per-section formula:  V_i (bbl) = ID² × L / 1029.4
export function multiSectionDrillStringVolume({ sections }) {
  const rows = _parseTable(sections);
  const stepLines = [];
  const perSection = {};
  let totalVol = 0;
  let totalLen = 0;
  const unrealistic = [];
  rows.forEach((r, idx) => {
    const t = r.tokens;
    let component, id, length;
    if (t.length === 2) {
      component = `Component ${idx + 1}`;
      id = _maybeNum(t[0]);
      length = _maybeNum(t[1]);
    } else if (t.length >= 3 && _maybeNum(t[1]) !== null && _maybeNum(t[2]) !== null) {
      component = t[0] || `Component ${idx + 1}`;
      id = _maybeNum(t[1]);
      length = _maybeNum(t[2]);
    } else if (t.length >= 3 && _maybeNum(t[0]) !== null && _maybeNum(t[1]) !== null) {
      id = _maybeNum(t[0]);
      length = _maybeNum(t[1]);
      component = t.slice(2).join(" ") || `Component ${idx + 1}`;
    } else {
      throw new Error(`Row ${idx + 1}: unable to parse — expected "componentType,ID,Length" or "ID,Length".`);
    }
    if (id === null || length === null) throw new Error(`Row ${idx + 1}: ID and Length must be numeric.`);
    if (id <= 0) throw new Error(`Row ${idx + 1}: ID must be > 0`);
    if (length <= 0) throw new Error(`Row ${idx + 1}: Length must be > 0`);
    if (id > 12) unrealistic.push(`Row ${idx + 1}: ID ${id} in is unusually large — please verify.`);
    if (length > 30000) unrealistic.push(`Row ${idx + 1}: Length ${length} ft is unusually long — please verify.`);
    const v = (id * id) * length / 1029.4;
    totalVol += v;
    totalLen += length;
    perSection[`${component} (bbl)`] = round(v, 3);
    stepLines.push(`Row ${idx + 1} · ${component} [ID ${id} in × ${length} ft] → ${id}² × ${length} / 1029.4 = ${round(v, 3)} bbl`);
  });
  const extras = {
    "Components counted": rows.length,
    "Total length (ft)": round(totalLen, 1),
    "Total drill string internal volume (bbl)": round(totalVol, 3),
    ...perSection,
  };
  if (unrealistic.length) extras["Validation warnings"] = unrealistic.join("  |  ");
  return {
    result: round(totalVol, 3),
    unit: "bbl",
    formula: "Σ  V_i = ID² × L_i / 1029.4   (one row per component)",
    steps: [
      "Per-component parse and volume:",
      ...stepLines,
      `Total drill string internal volume = Σ V_i = ${round(totalVol, 3)} bbl over ${round(totalLen, 1)} ft`,
    ],
    inputs: { "Rows entered": rows.length },
    extras,
    worksheetWarning: true,
  };
}

